import { browser } from '$app/environment';
import { derived, get, writable } from 'svelte/store';
import { APP_NAME, APP_VERSION, AUTHED_BASEPATH, BRAND, BUY_CRYPTO_URL, CHAT_CHANNELS, GIPHY_API_KEY, INFURA_KEY, LATE_FINGERPRINT, POSTAUTH_NOSESSION_BEHAVIOR, POSTHOG, REALM, RELEASE, RELEASE_STAGE, STAGE, TENOR_API_KEY, THEME, V2_PART, WALLET_CONNECT, WALLET_CONNECT_ENABLED } from './env';
import { AbTest, AppMode, appModes, BetbyBonusBehavior, CatalogFilter, DEFAULT_RANK_CONFIG, EndpointType, FeatureSet, ReleaseStage, Stage, Theme } from './types';
export { AbTest, AppMode, BetbyBonusBehavior, CatalogFilter, EndpointType, FeatureSet, RELEASE, ReleaseStage, Stage, Theme };
let realm;
let currentTheme;
let domainTheme;
let stage; // = (import.meta.env.VITE_STAGE ?? Stage.Stage) as string as Stage;
const languageOptions = [
    { code: 'ar', label: 'العربية', flag: 'AR' },
    { code: 'cs', label: 'Čeština', flag: 'CS' },
    { code: 'de', label: 'Deutsch', flag: 'DE' },
    { code: 'en', label: 'English', flag: 'EN' },
    { code: 'es', label: 'Español', flag: 'ES' },
    { code: 'fr', label: 'Français', flag: 'FR' },
    { code: 'id', label: 'Bahasa Indonesia', flag: 'ID' },
    { code: 'it', label: 'Italiano', flag: 'IT' },
    { code: 'ja', label: '日本語', flag: 'JA' },
    { code: 'ko', label: '한국어', flag: 'KO' },
    { code: 'ms', label: 'Bahasa Melayu', flag: 'MS' },
    { code: 'pt', label: 'Português', flag: 'PT' },
    { code: 'ru', label: 'Русский', flag: 'RU' },
    { code: 'tr', label: 'Türkçe', flag: 'TR' },
    { code: 'th', label: 'ไทย', flag: 'TH' },
    { code: 'vi', label: 'Tiếng Việt', flag: 'VI' },
    { code: 'zh', label: '中文', flag: 'ZH' }
];
export const GEO_GROUP1_RESTRICTED = [
    'US',
    'GB',
    'SE',
    'RO',
    'GR',
    'DK',
    'CZ',
    'NL',
    'CH',
    'BG',
    'CA'
];
export const GEO_SPORTS_RESTRICTED = ['US', 'IL', 'LV', 'SG'];
import { devEndpointSettings, localEndpointSettings } from './brands/dev';
import { endpointSetting as flushEndpointSetting, endpointSettings as flushEndpointSettings, getThemeSetting as flushThemeSetting } from './brands/flush';
import { endpointSetting as fomoEndpointSetting, endpointSettings as fomoEndpointSettings, getThemeSetting as fomoThemeSetting } from './brands/fomo';
import { endpointSetting as genericEndpointSetting, endpointSettings as genericEndpointSettings, getThemeSetting as genericThemeSetting } from './brands/generic';
import { endpointSetting as lunaEndpointSetting, endpointSettings as lunaEndpointSettings, getThemeSetting as lunaThemeSetting } from './brands/luna';
import { endpointSetting as rocketbetEndpointSetting, endpointSettings as rocketbetEndpointSettings, getThemeSetting as rocketbetThemeSetting } from './brands/rocket';
import { endpointSetting as turboslotsEndpointSetting, endpointSettings as turboslotsEndpointSettings, getThemeSetting as turboslotsThemeSetting } from './brands/turbo';
export const catalogBaseForTheme = (theme, isProd = true) => {
    if (!isProd) {
        const base = devEndpointSettings.find((s) => s.theme === theme)?.base;
        return `https://${base ?? 'monopoly-flush.net.infinitro.dev'}`;
    }
    const prodBase = {
        flush: flushEndpointSettings.find((s) => s.endpointType === EndpointType.Production)?.base ??
            'api-player.flush.com',
        fomo: fomoEndpointSettings[0]?.base ?? 'api.fomo.io',
        fomo_eu: fomoEndpointSettings[0]?.base ?? 'api.fomo.io',
        turboslots: turboslotsEndpointSettings[0]?.base ?? 'api.fomo.io',
        luna: lunaEndpointSettings[0]?.base ?? 'api.fomo.io',
        rocketbet: rocketbetEndpointSettings[0]?.base ?? 'monopoly-rocket.prod.infinitro.dev',
        generic: genericEndpointSettings[0]?.base ?? 'api-public.flush.com'
    };
    return `https://${prodBase[theme] ?? prodBase['flush']}`;
};
export const themeSettingForBuild = (theme, brand) => {
    const b = brand ?? '';
    switch (theme) {
        case Theme.Fomo:
            return fomoThemeSetting(b, b);
        case Theme.Luna:
            return lunaThemeSetting(b, b);
        case Theme.Rocketbet:
            return rocketbetThemeSetting(b, b);
        case Theme.Turboslots:
            return turboslotsThemeSetting(b, b);
        case Theme.Generic:
            return genericThemeSetting(b, b);
        default:
            return flushThemeSetting(b, b);
    }
};
export const appName = APP_NAME ?? '';
export const appVersion = APP_VERSION ?? '';
export const appMode = (appName.endsWith('/player')
    ? AppMode.Player
    : appName.endsWith('/mod')
        ? AppMode.Mod
        : appName.endsWith('/public')
            ? AppMode.Public
            : AppMode.Library);
const getFullDomain = () => {
    if (browser) {
        try {
            const host = window?.location?.hostname?.toLowerCase() || '';
            // For preview/dev environments, use VITE_BRAND if hostname doesn't match production domains
            if (host.includes('previewenv.net') || host === 'localhost' || host === '127.0.0.1') {
                return BRAND || host;
            }
            return host;
        }
        catch {
            // ignore
        }
    }
    return BRAND || '';
};
const getDomain = () => {
    if (browser) {
        try {
            const host = window?.location?.hostname?.toLowerCase() || '';
            if (host.endsWith('fomo.io'))
                return 'fomo.io';
            if (host.endsWith('flush.com'))
                return 'flush.com';
            if (host.endsWith('flushgames.net'))
                return 'flushgames.net';
            if (host.endsWith('previewenv.net'))
                return 'previewenv.net';
            if (host.endsWith('rocketbet.com'))
                return 'rocketbet.com';
            if (host.endsWith('turboslot.com'))
                return 'turboslot.com';
            if (host.endsWith('lunabet.com'))
                return 'lunabet.com';
            return host;
        }
        catch {
            // ignore
        }
    }
    return '';
};
const getSafeStorage = (key) => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined')
        return undefined;
    try {
        return localStorage.getItem(key);
    }
    catch {
        return null;
    }
};
const setSafeStorage = (key, value) => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined')
        return;
    try {
        localStorage.setItem(key, value);
    }
    catch {
        // ignore
    }
};
const clearSafeStorage = (key) => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined')
        return;
    try {
        localStorage.removeItem(key);
    }
    catch {
        // ignore
    }
};
const getLastTheme = () => {
    let lastTheme = getSafeStorage('inf-theme');
    if (lastTheme && Object.values(Theme).includes(lastTheme)) {
        return lastTheme;
    }
    if (lastTheme) {
        clearSafeStorage('inf-theme');
    }
    return undefined;
};
const getInitialTheme = () => {
    if (RELEASE === Stage.Prod) {
        // compile-time theme
        domainTheme = THEME;
        currentTheme = THEME;
        return currentTheme;
    }
    const domain = getDomain();
    const lastSelectedTheme = getLastTheme();
    switch (domain) {
        case 'fomo.io':
            domainTheme = Theme.Fomo;
            currentTheme = Theme.Fomo;
            break;
        case 'flush.com':
            domainTheme = Theme.Flush;
            currentTheme = Theme.Flush;
            break;
        case 'rocketbet.com':
            domainTheme = Theme.Rocketbet;
            currentTheme = Theme.Rocketbet;
            break;
        case 'turboslot.com':
            domainTheme = Theme.Turboslots;
            currentTheme = Theme.Turboslots;
            break;
        case 'lunabet.com':
            domainTheme = Theme.Luna;
            currentTheme = Theme.Luna;
            break;
        case '127.0.0.1':
        case 'localhost':
        case 'previewenv.net':
        default:
            // todo: add runtime theme selection
            domainTheme = Theme.Dynamic;
            if (lastSelectedTheme) {
                if (Object.values(Theme).includes(lastSelectedTheme)) {
                    currentTheme = lastSelectedTheme;
                }
            }
            else {
                currentTheme = THEME || Theme.Flush;
                if (domain.includes('luna'))
                    currentTheme = Theme.Luna;
                if (domain.includes('flush'))
                    currentTheme = Theme.Flush;
                if (domain.includes('fomo'))
                    currentTheme = Theme.Fomo;
                if (domain.includes('luna'))
                    currentTheme = Theme.Luna;
                if (domain.includes('rocket'))
                    currentTheme = Theme.Rocketbet;
                if (domain.includes('turbo'))
                    currentTheme = Theme.Turboslots;
                if (domain.includes('generic'))
                    currentTheme = Theme.Generic;
            }
            break;
    }
    return currentTheme;
};
export const getTheme = () => {
    if (!currentTheme)
        currentTheme = getInitialTheme();
    return currentTheme;
};
export const getThemes = () => {
    if (!currentTheme)
        currentTheme = getInitialTheme();
    return domainTheme === Theme.Dynamic
        ? [Theme.Flush, Theme.Fomo, Theme.Generic, Theme.Luna, Theme.Rocketbet, Theme.Turboslots]
        : [currentTheme];
};
export const getThemeSetting = () => {
    const domain = getDomain();
    const fullDomain = getFullDomain();
    if (!currentTheme)
        currentTheme = getInitialTheme();
    switch (currentTheme) {
        case Theme.Flush:
            return flushThemeSetting(domain, fullDomain);
        case Theme.Fomo:
            return fomoThemeSetting(domain, fullDomain);
        case Theme.Luna:
            return lunaThemeSetting(domain, fullDomain);
        case Theme.Rocketbet:
            return rocketbetThemeSetting(domain, fullDomain);
        case Theme.Turboslots:
            return turboslotsThemeSetting(domain, fullDomain);
        case Theme.Generic:
            return genericThemeSetting(domain, fullDomain);
        default:
            throw new Error(`Unknown current theme: ${currentTheme}`);
    }
};
/** Rank config for the current theme. Uses DEFAULT_RANK_CONFIG when theme has no rankConfig. */
export const getRankConfig = () => getThemeSetting().rankConfig ?? DEFAULT_RANK_CONFIG;
export const stateTheme = writable(getTheme());
export const stateThemeSetting = derived([stateTheme], ([$stateTheme]) => {
    const domain = getDomain();
    const fullDomain = getFullDomain();
    switch ($stateTheme) {
        case Theme.Flush:
            return flushThemeSetting(domain, fullDomain);
        case Theme.Fomo:
            return fomoThemeSetting(domain, fullDomain);
        case Theme.Luna:
            return lunaThemeSetting(domain, fullDomain);
        case Theme.Rocketbet:
            return rocketbetThemeSetting(domain, fullDomain);
        case Theme.Turboslots:
            return turboslotsThemeSetting(domain, fullDomain);
        case Theme.Generic:
            return genericThemeSetting(domain, fullDomain);
        default:
            throw new Error(`Unknown theme: ${$stateTheme}`);
    }
});
export const stateLanguageOptions = derived([stateThemeSetting], ([$stateThemeSetting]) => languageOptions.filter((option) => $stateThemeSetting.languages.includes(option.code)));
const getLastEndpointType = () => {
    const lastEndpointType = getSafeStorage('endpointType');
    if (lastEndpointType && Object.values(EndpointType).includes(lastEndpointType)) {
        return lastEndpointType;
    }
    if (lastEndpointType) {
        clearSafeStorage('endpointType');
    }
    return undefined;
};
export const getEndpointOptions = (inTheme) => {
    inTheme = inTheme ?? currentTheme;
    if (STAGE === Stage.Local)
        return [localEndpointSettings];
    if (!inTheme)
        inTheme = getInitialTheme();
    switch (domainTheme) {
        case Theme.Flush:
            return [flushEndpointSetting(appMode, getDomain(), getFullDomain())];
        case Theme.Fomo:
            return [fomoEndpointSetting(appMode, getDomain(), getFullDomain())];
        case Theme.Luna:
            return [lunaEndpointSetting(appMode, getDomain(), getFullDomain())];
        case Theme.Rocketbet:
            return [rocketbetEndpointSetting(appMode, getDomain(), getFullDomain())];
        case Theme.Turboslots:
            return [turboslotsEndpointSetting(appMode, getDomain(), getFullDomain())];
        case Theme.Generic:
            return [genericEndpointSetting(appMode, getDomain(), getFullDomain())];
        case Theme.Dynamic:
            if (RELEASE === Stage.Prod) {
                throw new Error(`Dynamic themes not supported for Prod release`);
            }
            break;
        default:
            if (RELEASE == Stage.Prod) {
                throw new Error(`Unknown domain theme: ${domainTheme}`);
            }
    }
    if (inTheme == Theme.Dynamic)
        inTheme = THEME || Theme.Flush;
    let options = [];
    switch (inTheme) {
        case Theme.Flush:
            options = devEndpointSettings.filter((e) => e.theme === Theme.Flush);
            options.push(...flushEndpointSettings);
            break;
        case Theme.Fomo:
            options = devEndpointSettings.filter((e) => e.theme === Theme.Fomo);
            options.push(...fomoEndpointSettings);
            break;
        case Theme.Luna:
            options = devEndpointSettings.filter((e) => e.theme === Theme.Luna);
            options.push(...lunaEndpointSettings);
            break;
        case Theme.Rocketbet:
            options = devEndpointSettings.filter((e) => e.theme === Theme.Rocketbet);
            options.push(...rocketbetEndpointSettings);
            break;
        case Theme.Turboslots:
            options = devEndpointSettings.filter((e) => e.theme === Theme.Turboslots);
            options.push(...turboslotsEndpointSettings);
            break;
        case Theme.Generic:
            options = devEndpointSettings.filter((e) => e.theme === Theme.Flush);
            options.push(...genericEndpointSettings);
            break;
        default:
            throw new Error(`Unknown theme: ${inTheme}`);
    }
    return options;
};
const getInitialEndpointSetting = () => {
    const options = getEndpointOptions();
    if (options.length === 1)
        return options[0];
    if (options.length === 0)
        throw new Error(`No endpoints found for: ${currentTheme}`);
    const lastEndpointType = getLastEndpointType();
    const e = options.find((e) => !lastEndpointType || e.endpointType === lastEndpointType);
    if (e)
        return e;
    return options[0];
};
let currentEndpoint;
export const getEndpoint = () => {
    if (!currentEndpoint)
        currentEndpoint = getInitialEndpointSetting();
    return currentEndpoint;
};
export const getRealm = () => {
    if (!realm) {
        if (REALM) {
            realm = REALM;
            return realm;
        }
        realm = getEndpoint().realm;
    }
    return realm;
};
export const getEndPoints = () => {
    if (!currentTheme)
        currentTheme = getInitialTheme();
    return devEndpointSettings.filter((e) => e.theme === currentTheme);
};
export const cfgAppName = () => APP_NAME;
export const cfgAppVersion = () => APP_VERSION;
export const cfgAppMode = () => appMode;
export const cfgAuthedBasePath = () => {
    const basePath = AUTHED_BASEPATH && appModes.includes(AUTHED_BASEPATH.toLowerCase())
        ? AUTHED_BASEPATH
        : appMode == AppMode.Library
            ? ''
            : AppMode.Player;
    return basePath;
};
export const cfgTheme = () => getTheme();
let _theme = cfgTheme();
export const randomAbTest = () => {
    const v = Math.floor(Math.random() * 4) + 1;
    switch (v) {
        case 1:
            return AbTest.A;
        case 2:
            return AbTest.B;
        case 3:
            return AbTest.C;
        default:
            return AbTest.D;
    }
};
export const cfgAbTest = () => {
    const lastAbTest = getSafeStorage('abTest');
    if (lastAbTest)
        return lastAbTest;
    const newAbTest = randomAbTest();
    setSafeStorage('abTest', newAbTest);
    return newAbTest;
};
export const cfgEndpointType = () => {
    return getEndpoint().endpointType;
};
export const cfgCatalogFilter = () => {
    if (RELEASE === Stage.Prod)
        return CatalogFilter.ReleaseGames;
    const lastCatalogFilter = getSafeStorage('catalogFilter');
    if (lastCatalogFilter)
        return lastCatalogFilter;
    if (getEndpoint().catalogFilter)
        return getEndpoint().catalogFilter;
    const domain = getFullDomain();
    if (domain.includes('alpha'))
        return CatalogFilter.AllProviders;
    if (domain.includes('beta'))
        return CatalogFilter.AllGames;
    if (domain.includes('gamma'))
        return CatalogFilter.ReleaseGames;
    return CatalogFilter.AllProviders;
};
export const cfgFeatures = () => {
    if (RELEASE === Stage.Prod)
        return FeatureSet.Release;
    const last = getSafeStorage('featureSet');
    if (last)
        return last;
    const domain = getFullDomain();
    if (domain.includes('alpha'))
        return FeatureSet.Preview;
    if (domain.includes('beta'))
        return FeatureSet.Release;
    if (domain.includes('gamma'))
        return FeatureSet.Release;
    return FeatureSet.Preview;
};
export const cfgReleaseStage = () => {
    if (RELEASE_STAGE)
        return RELEASE_STAGE;
    const last = getSafeStorage('releaseStage');
    if (last)
        return last;
    const domain = getFullDomain();
    if (appMode === AppMode.Mod || domain.includes('playeradmin'))
        return ReleaseStage.Internal;
    if (domain.includes('gamma'))
        return ReleaseStage.Gamma;
    if (domain.includes('early'))
        return ReleaseStage.EarlyAccess;
    if (RELEASE === Stage.Prod)
        return ReleaseStage.General;
    if (domain.includes('alpha') ||
        domain.includes('localhost') ||
        domain.includes('127.0.0.1') ||
        domain.includes('dev'))
        return ReleaseStage.Alpha;
    if (domain.includes('beta'))
        return ReleaseStage.Beta;
    return ReleaseStage.General;
};
let _releaseStage = cfgReleaseStage();
export const stateReleaseStage = writable(_releaseStage);
stateReleaseStage.subscribe((releaseStage) => {
    if (_releaseStage === releaseStage)
        return;
    if (_releaseStage) {
        // only store releseStage preference if it was explicitly changed
        setSafeStorage('releaseStage', releaseStage);
    }
    _releaseStage = releaseStage;
});
export const wantThemes = (wanted) => {
    const current = getTheme();
    return wanted.includes(current);
};
export const wantAtLeastReleaseStage = (wanted) => {
    const current = _releaseStage;
    switch (wanted) {
        case ReleaseStage.Alpha:
            return [ReleaseStage.Alpha].includes(current);
        case ReleaseStage.Beta:
            return [ReleaseStage.Alpha, ReleaseStage.Beta].includes(current);
        case ReleaseStage.Gamma:
            return [ReleaseStage.Alpha, ReleaseStage.Beta, ReleaseStage.Gamma].includes(current);
        case ReleaseStage.Internal:
            return [
                ReleaseStage.Alpha,
                ReleaseStage.Beta,
                ReleaseStage.Gamma,
                ReleaseStage.Internal
            ].includes(current);
        case ReleaseStage.EarlyAccess:
            return [
                ReleaseStage.Alpha,
                ReleaseStage.Beta,
                ReleaseStage.Gamma,
                ReleaseStage.Internal,
                ReleaseStage.EarlyAccess
            ].includes(current);
        case ReleaseStage.General:
            return true;
    }
};
export const wantAtLeastReleaseStageAndThemes = (wanted, themes) => {
    return wantAtLeastReleaseStage(wanted) && wantThemes(themes);
};
export const cfgStage = () => {
    if (RELEASE) {
        stage = RELEASE;
        return stage;
    }
    if (!stage)
        stage = Stage.Stage;
    return stage;
};
// brand settings
export const cfgBetbyThemeName = () => getThemeSetting().betbyThemeName ?? 'default';
export const cfgBetbyBonusBehavior = () => getThemeSetting().betbyBonusBehavior;
export const cfgBaseCurrency = () => getEndpoint().baseCurrency;
export const cfgBaseCurrencySymbol = () => getEndpoint().baseCurrencySymbol;
export const cfgBrand = () => getThemeSetting().brand;
export const cfgBrandTitle = () => getThemeSetting().brandTitle;
export const cfgCanonicalUrl = () => getThemeSetting().canonicalUrl;
export const cfgHelpcenterUrl = () => getThemeSetting().helpcenterHref ?? '/helpcenter';
export const cfgLanguageRedirects = () => getThemeSetting().languageRedirects ?? {};
export const cfgNoIndex = () => getThemeSetting().noIndex || getEndpoint().endpointType !== EndpointType.Production;
export const cfgCdnBase = () => getThemeSetting().cdnBase;
export const cfgCdnTiles = () => getThemeSetting().cdnTiles;
export const cfgCdnBanners = () => getThemeSetting().cdnBanners;
export const cfgDiscordLink = () => getThemeSetting().discordLink;
export const cfgGoogleAnalytics = () => getThemeSetting().googleAnalytics;
export const cfgGoogleClientId = () => getThemeSetting().googleClientId;
export const cfgGoogleTagManager = () => getThemeSetting().googleTagManager;
export const cfgIntercomId = () => getThemeSetting().intercomId;
export const cfgTelegramLink = () => getThemeSetting().telegramLink;
export const cfgFacebookLink = () => getThemeSetting().facebookLink;
export const cfgInstagramLink = () => getThemeSetting().instagramLink;
export const cfgYoutubeLink = () => getThemeSetting().youtubeLink;
export const cfgXLink = () => getThemeSetting().xLink;
export const cfgGamityEnvironment = () => getEndpoint().gamityEnvironment ?? '';
export const cfgGamityBrandRef = () => getEndpoint().gamityBrandRef ?? '';
export const cfgGamityCurrency = () => getEndpoint().gamityCurrency ?? '';
export const cfgGamityIntegrationRef = () => getEndpoint().integrationRef ?? '';
export const cfgSupportChatApiUrl = () => getEndpoint().supportChatApiUrl ?? '';
export const cfgFingerprintjs = () => getThemeSetting().fingerprintjs;
export const cfgFingerprintjsHost = () => getThemeSetting().fingerprintjsHost;
export const cfgReownProjectId = () => getEndpoint().reownProjectId;
export const cfgOptimoveTenantId = () => getEndpoint().optimoveTenantId;
export const cfgOptimoveTenantToken = () => getEndpoint().optimoveTenantToken;
export const cfgOptimoveEnabled = () => !!(cfgOptimoveTenantId() && cfgOptimoveTenantToken());
export const cfgMicrosoftClarityId = () => getEndpoint().microsoftClarityId;
export const cfgSupportEmail = () => getThemeSetting().supportEmail ?? `support@${cfgBrand().toLowerCase()}`;
export const cfgThemePath = () => getThemeSetting().themePath ?? _theme.toString().toLowerCase();
export const cfgThemeVerbiageLabel = () => getThemeSetting().verbiageLabel ?? _theme.toString().toLowerCase();
// endpoint settings
export const cfgEndpointBase = (v, ws = false) => {
    if (v.baseLocal && (v.baseInsecure || v.basePort !== 80)) {
        if (v.baseInsecure) {
            if (ws)
                return `ws://${v.base}:${v.basePort}`;
            return `http://${v.base}:${v.basePort}`;
        }
        if (ws)
            return `wss://${v.base}:${v.basePort}`;
        return `https://${v.base}:${v.basePort}`;
    }
    if (ws)
        return `wss://${v.base}`;
    return `https://${v.base}`;
};
export const cfgBetbyBrandId = () => getEndpoint().betbyBrandId;
export const cfgChatApiUrl = () => getEndpoint().chatApiUrl ?? `${cfgEndpointBase(getEndpoint())}/chat/`;
export const cfgChatSubscriptionUrl = () => getEndpoint().chatSubscriptionUrl ?? `${cfgEndpointBase(getEndpoint(), true)}/chat`;
export const cfgNotifyApiUrl = () => getEndpoint().notifyApiUrl ?? `${cfgEndpointBase(getEndpoint())}/notifications`;
export const cfgNotifySubscriptionUrl = () => getEndpoint().notifySubscriptionUrl ?? `${cfgEndpointBase(getEndpoint(), true)}/notifications`;
export const cfgApiBase = () => getEndpoint().base;
export const cfgApiPlayerSubscriptionUrl = () => getEndpoint().apiPlayerSubscriptionUrl ?? `${cfgEndpointBase(getEndpoint(), true)}/player`;
export const cfgApiPlayerUrl = () => getEndpoint().apiPlayerUrl ?? `${cfgEndpointBase(getEndpoint())}/player`;
export const cfgApiPublicSubscriptionUrl = () => getEndpoint().apiPublicSubscriptionUrl ?? `${cfgEndpointBase(getEndpoint(), true)}/public`;
export const cfgApiPublicUrl = () => getEndpoint().apiPublicUrl ?? `${cfgEndpointBase(getEndpoint())}/public`;
// static settings
export const cfgBetbyScriptUrl = () => getEndpoint().betbyScriptUrl ?? '';
export const cfgBuyCryptoUrl = () => BUY_CRYPTO_URL;
export const cfgChatChannels = () => CHAT_CHANNELS; // todo: deprecate and move
export const cfgGiphyApiKey = () => GIPHY_API_KEY;
export const cfgTenorApiKey = () => TENOR_API_KEY;
export const cfgInfuraKey = () => INFURA_KEY;
export const cfgLateFingerprint = () => LATE_FINGERPRINT;
export const cfgHasSports = () => getThemeSetting().betbyEnabled === true && getEndpoint().betbyEnabled === true;
export const cfgPosthog = () => POSTHOG;
export const cfgWalletConnect = () => WALLET_CONNECT; // move to brand
export const cfgWalletConnectEnabled = () => WALLET_CONNECT_ENABLED;
// manual tweaks/configurations
export const cfgPostauthNoSessionBehavior = () => POSTAUTH_NOSESSION_BEHAVIOR; // todo: deprecate
export const cfgV2Part = () => V2_PART; // public V2 player V1
// ui settings
export const stateUi = derived(stateThemeSetting, ($s) => {
    const ui = $s.ui;
    const useCashierV4 = ui?.cashier?.useCashierV4 ?? $s.useCashierV4 ?? false;
    if (!ui) {
        return {
            card: { localizedTileLocales: $s.languagesTiles },
            cashier: {
                useCashierV4,
                useCompactDepositAddressOptionLayout: false,
                useUsdtForDepositConversionRateSendInput: false,
                extendCashierVerbiageKeysWithBrandLabel: false,
                showProgressBarBonusMoney: true
            }
        };
    }
    return {
        ...ui,
        card: {
            ...(ui.card ?? {}),
            localizedTileLocales: ui.card?.localizedTileLocales ?? $s.languagesTiles
        },
        cashier: { ...ui.cashier, useCashierV4 }
    };
});
// dyanmic states for development
export const stateRealm = writable(getRealm());
export const stateFeatureSet = writable(cfgFeatures());
export const stateAbTest = writable(cfgAbTest());
export const stateEndpointType = writable(cfgEndpointType());
export const stateCatalogFilter = writable(cfgCatalogFilter());
export const stateCatalogLanguage = writable('en');
export const stateCountry = writable(browser && localStorage ? localStorage.getItem('country') : null);
export const stateHasSports = derived([stateCountry], ([$stateCountry]) => cfgHasSports() && $stateCountry && !GEO_SPORTS_RESTRICTED.includes($stateCountry));
/** True when `ui.affiliates.enabled` and optional `rel` both allow affiliates for the active feature set. */
export const stateAffiliatesEnabled = derived([stateUi, stateFeatureSet], ([$ui, $featureSet]) => {
    const affiliates = $ui.affiliates;
    if (affiliates?.enabled !== true)
        return false;
    const rel = affiliates.rel;
    if (rel == null)
        return true;
    return rel === $featureSet;
});
export const cfgAffiliatesEnabled = () => get(stateAffiliatesEnabled);
// TODO: re-enable game geoblocking (stateGeoGamePending + stateGeoGameRestricted)
export const stateGeoGamePending = derived([stateCountry], (_) => false);
export const stateGeoGameRestricted = derived([stateCountry], (_) => false);
export const cfgToThemeCdnPath = (path) => {
    return `${cfgCdnBase()}/images${path}`;
};
export const cfgToThemeContentPath = (path) => {
    return `${cfgCdnBase()}/content${path}`;
};
export const COUNTRIES_GEO_GAME_RESTRICTED = [
    'BG',
    'CA',
    'CH',
    'CZ',
    'DK',
    'GB',
    'GR',
    'NL',
    'RO',
    'SE',
    'US'
];
let _country = null;
stateCountry.subscribe((value) => {
    if (_country === value || !value)
        return;
    if (browser && localStorage)
        localStorage.setItem('country', value);
    _country = value;
});
stateTheme.subscribe((theme) => {
    if (_theme === theme)
        return;
    const previousTheme = _theme;
    if (previousTheme) {
        try {
            if (browser) {
                document.documentElement.classList.remove(previousTheme.toString().toLowerCase());
            }
        }
        catch (e) {
            console.log(`error removing previous theme ${previousTheme}`);
        }
        // only set theme selection if it was changed
        setSafeStorage('inf-theme', theme);
        if (browser) {
            window.location.reload(); // forces theme to be applied
        }
    }
    _theme = theme;
    try {
        if (browser) {
            document.documentElement.classList.add(theme.toString().toLowerCase());
            console.log(`set documentElement theme to ${_theme}`);
        }
    }
    catch (e) {
        console.log(`error setting documentElement theme to ${_theme}`);
    }
});
let _featureSet;
stateFeatureSet.subscribe((featureSet) => {
    if (_featureSet === featureSet)
        return;
    if (_featureSet) {
        // only store featureSet preference if it was explicitly changed
        setSafeStorage('featureSet', featureSet);
    }
    _featureSet = featureSet;
});
let _endpointType;
stateEndpointType.subscribe((endpointType) => {
    if (_endpointType === endpointType)
        return;
    if (_endpointType) {
        // only store preference if it was explicitly changed
        setSafeStorage('endpointType', endpointType);
    }
    _endpointType = endpointType;
});
let _catalogFilter;
stateCatalogFilter.subscribe((catalogFilter) => {
    if (_catalogFilter === catalogFilter)
        return;
    if (_catalogFilter) {
        // only store preference if it was explicitly changed
        setSafeStorage('catalogFilter', catalogFilter);
    }
    _catalogFilter = catalogFilter;
});
let _abTest;
stateAbTest.subscribe((abTest) => {
    if (_abTest === abTest)
        return;
    if (_abTest) {
        // only store preference if it was explicitly changed
        setSafeStorage('abTest', abTest);
    }
    _abTest = abTest;
});
export const toImageCdn = (file) => {
    return `${cfgCdnBase()}/images/${file}`;
};
export const toTileCdn = (file) => {
    return `${cfgCdnTiles()}/${file}`;
};
export const toThemeCdn = (file) => {
    return `${cfgCdnBase()}/${file}`;
};
export const toContentCdn = (file) => {
    return `${cfgCdnBase()}/${file}`;
};
export const getGameSetLimit = () => {
    return getThemeSetting().gameSetLimit ?? 300;
};
export const cfgNewGamesIncludeLive = () => {
    return getThemeSetting().newGamesIncludeLive ?? false;
};
// game state management
export const gameDemoMode = writable(false);
// value must be lesser than noDecimalAmount to show decimals
// set to -1 to always show decimals
export const formatBaseCurrency = (value, withSymbol = false, noDecimalAmount = 10, alwaysShowDecimals = false, compact = false) => {
    if (isNaN(value))
        return '';
    const digits = value >= noDecimalAmount && value == Math.floor(value) && !alwaysShowDecimals
        ? 0
        : getEndpoint().baseCurrencyDigits;
    const symbol = withSymbol ? getEndpoint().baseCurrencySymbol : '';
    return `${symbol}${new Intl.NumberFormat(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        notation: compact ? 'compact' : 'standard'
    }).format(value)}`;
};
/**
 * Checks if the active event theme should be displayed
 * @returns true if:
 * - We're in a preview feature set (not in production release)
 * - Current date is within the event's start and end dates
 * - activeEventTheme is configured in the theme settings
 */
export const isEventThemeActive = () => {
    // Only allow event themes on Flush
    if (getTheme() !== Theme.Flush) {
        return false;
    }
    // Get the active event theme from settings
    const themeSetting = getThemeSetting();
    const activeEventTheme = themeSetting.ui?.activeEventTheme;
    if (!activeEventTheme?.eventStartDate || !activeEventTheme?.eventEndDate) {
        return false;
    }
    // Check if current date is within event dates
    const now = new Date();
    const startDate = new Date(activeEventTheme.eventStartDate);
    const endDate = new Date(activeEventTheme.eventEndDate);
    return now >= startDate && now <= endDate;
};
/**
 * Gets the CDN path for an event theme asset
 * @param assetName - Name of the asset file (e.g., 'divider.png', 'buttonaddon.png', 'logo.png')
 * @returns Full CDN URL for the event theme asset, or empty string if event theme is not active
 */
export const cfgEventThemeCdnPath = (assetName) => {
    const themeSetting = getThemeSetting();
    const activeEventTheme = themeSetting.ui?.activeEventTheme;
    if (!activeEventTheme?.eventID) {
        return '';
    }
    return `${cfgCdnBase()}/event-theming/${activeEventTheme.eventID}/${assetName}`;
};
/**
 * Checks if a specific event theme asset is enabled
 * Includes check for event theme being active (date range and feature set)
 * @param assetKey - The config key for the asset (e.g., 'pageBackground', 'buttonAddon', 'customLogo')
 * @returns true if the event theme is active and the specific asset is enabled, false otherwise
 */
export const isEventAssetEnabled = (assetKey) => {
    // First check if event theme is active
    if (!isEventThemeActive()) {
        return false;
    }
    const themeSetting = getThemeSetting();
    const activeEventTheme = themeSetting.ui?.activeEventTheme;
    if (!activeEventTheme?.config) {
        return false;
    }
    return activeEventTheme.config[assetKey] ?? false;
};
export const isRacesEnabled = () => {
    return getThemeSetting().racesEnabled ?? false;
};
